import striptags from 'striptags';
import { expandTerms, extractTerms, matchesQueryFuzzy } from './fuzzy';
import { isContentFeature } from './utils';
import { ContentFeatureModel, SectionModel } from '../../fetch-data/graphql';

export function extractFirstParagraph(text: string): string {
  if (!text) return '';

  // Split by double newlines or periods followed by space/newline
  const paragraphs = text.split(/\n\n+|\.\s+/);
  const firstParagraph = paragraphs[0]?.trim() || '';

  // If first paragraph is too short, try to get more
  if (firstParagraph.length < 50 && paragraphs.length > 1) {
    return (firstParagraph + '. ' + paragraphs[1]).trim();
  }

  // Limit to reasonable length
  return firstParagraph.length > 300
    ? firstParagraph.substring(0, 300) + '...'
    : firstParagraph;
}

export function findMatchingMarkdownTitle(
  markdown: string | null | undefined,
  plaintext: string,
  searchTerm: string,
  html: string | null | undefined,
  useFuzzy: boolean = false
): {
  title: { html: string | null; plaintext: string } | null;
  paragraph: string;
} {
  if (!markdown || !plaintext) {
    return { title: null, paragraph: '' };
  }

  const lowerSearchTerm = searchTerm.toLowerCase();
  const markdownLines = markdown.split('\n');
  const plaintextLines = plaintext.split('\n');

  // Find ALL matching titles and score them
  const matchingTitles: Array<{
    title: { html: string | null; plaintext: string };
    paragraph: string;
    score: number;
    index: number;
  }> = [];

  // Only match markdown headers (###) - these are actual titles
  // Ignore bold text as it can be in list items or regular content
  for (let i = 0; i < markdownLines.length; i++) {
    const line = markdownLines[i].trim();
    if (!line) continue;

    // Only check for markdown headers, not bold text
    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      const titleText = headerMatch[1]
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '') // Remove markdown images: ![alt](url)
        .trim();

      const lowerTitleText = titleText.toLowerCase();

      // Check if the title contains the search term (fuzzy or exact)
      const matches = useFuzzy
        ? matchesQueryFuzzy(titleText, searchTerm)
        : lowerTitleText.includes(lowerSearchTerm);

      if (!matches) {
        continue;
      }

      // If HTML is available, verify it corresponds to an actual HTML header tag
      if (html) {
        const htmlHeaderRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi;
        let found = false;
        let match;

        while ((match = htmlHeaderRegex.exec(html)) !== null) {
          const headerText = striptags(match[1]).trim();
          // Use stricter matching - the texts should be very similar
          const normalizedMarkdown = titleText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();
          const normalizedHtml = headerText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();

          if (
            normalizedHtml === normalizedMarkdown ||
            normalizedHtml.includes(normalizedMarkdown) ||
            normalizedMarkdown.includes(normalizedHtml)
          ) {
            found = true;
            break;
          }
        }

        // Only proceed if we found a corresponding HTML header tag
        if (!found) continue;
      }

      // Score the match: exact phrase match gets highest score
      let score = 0;
      if (lowerTitleText === lowerSearchTerm) {
        score = 1000; // Exact match
      } else if (lowerTitleText.startsWith(lowerSearchTerm)) {
        score = 800; // Starts with search term
      } else {
        // Check for exact phrase with word boundaries
        const escapedSearch = lowerSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const phraseRegex = new RegExp(`\\b${escapedSearch}\\b`, 'i');
        if (phraseRegex.test(titleText)) {
          score = 700; // Exact phrase match
        } else if (lowerTitleText.includes(lowerSearchTerm)) {
          score = 500; // Contains search term
        } else {
          score = 300; // Fuzzy match
        }
      }

      // Extract paragraph after THIS specific title (index i)
      // Must stop at the next header to avoid getting content from other titles
      let paragraph = '';
      let contentStart = i + 1;
      
      // Skip empty lines immediately after this title
      while (
        contentStart < markdownLines.length &&
        markdownLines[contentStart].trim() === ''
      ) {
        contentStart++;
      }

      // Find the next header to stop extraction there
      let contentEnd = markdownLines.length;
      for (let j = contentStart; j < markdownLines.length; j++) {
        const line = markdownLines[j].trim();
        // Stop at the next header (h1-h6)
        if (line.match(/^#{1,6}\s+/)) {
          contentEnd = j;
          break;
        }
      }

      // Extract content between this title and the next header
      if (contentStart < contentEnd) {
        // First, collect ALL titles from the markdown to filter them out
        const allTitlesInMarkdown = new Set<string>();
        for (let j = 0; j < markdownLines.length; j++) {
          const line = markdownLines[j].trim();
          const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
          if (headerMatch) {
            const titleText = headerMatch[1]
              .replace(/\*\*/g, '')
              .replace(/\*/g, '')
              .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
              .trim()
              .toLowerCase();
            allTitlesInMarkdown.add(titleText);
          }
        }
        
        // Use plaintext directly - find the title position and extract content after it
        const titleInPlaintext = plaintext.toLowerCase().indexOf(titleText.toLowerCase());
        if (titleInPlaintext !== -1) {
          // Find content after this title
          let plaintextContentStart = titleInPlaintext + titleText.length;
          
          // Skip whitespace and newlines after the title
          while (plaintextContentStart < plaintext.length && 
                 /\s/.test(plaintext[plaintextContentStart])) {
            plaintextContentStart++;
          }
          
          // Find the next markdown header in plaintext
          let plaintextContentEnd = plaintext.length;
          for (let j = i + 1; j < markdownLines.length; j++) {
            const nextLine = markdownLines[j].trim();
            if (nextLine.match(/^#{1,6}\s+/)) {
              const nextHeaderMatch = nextLine.match(/^#{1,6}\s+(.+)$/);
              if (nextHeaderMatch) {
                const nextTitleText = nextHeaderMatch[1]
                  .replace(/\*\*/g, '')
                  .replace(/\*/g, '')
                  .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
                  .trim();
                const nextTitleIndex = plaintext.toLowerCase().indexOf(
                  nextTitleText.toLowerCase(),
                  plaintextContentStart
                );
                if (nextTitleIndex !== -1) {
                  plaintextContentEnd = nextTitleIndex;
                  break;
                }
              }
            }
          }
          
          const contentText = plaintext.substring(plaintextContentStart, plaintextContentEnd).trim();
          
          if (contentText) {
            // Split into paragraphs
            let allParagraphs = contentText.split(/\n\n+/);
            
            // If no double newlines found, treat entire content as one paragraph
            if (allParagraphs.length === 1 && !contentText.includes('\n\n')) {
              allParagraphs = [contentText];
            }
            
            for (const p of allParagraphs) {
              const trimmed = p.trim().replace(/\n+/g, ' '); // Normalize newlines to spaces
              
              // Skip if empty or too short
              if (trimmed.length === 0 || trimmed.length < 20) continue;
              
              // Skip if it exactly matches any title from the markdown
              const lowerTrimmed = trimmed.toLowerCase();
              let isTitle = false;
              for (const title of allTitlesInMarkdown) {
                // Only filter exact matches or very short text that matches a title
                if (lowerTrimmed === title || (trimmed.length < 50 && lowerTrimmed === title)) {
                  isTitle = true;
                  break;
                }
              }
              if (isTitle) continue;
              
              // Skip if it looks like a title (all caps, very short)
              if (trimmed.match(/^[A-Z\s]{0,50}$/)) continue;
              
              // This looks like actual content paragraph
              paragraph = trimmed;
              // Limit length
              if (paragraph.length > 300) {
                paragraph = paragraph.substring(0, 300) + '...';
              }
              break;
            }
            
            // Fallback: if no good paragraph found, use first substantial non-title text
            if (!paragraph) {
              for (const p of allParagraphs) {
                const trimmed = p.trim().replace(/\n+/g, ' ');
                if (trimmed.length >= 20) {
                  const lowerTrimmed = trimmed.toLowerCase();
                  let isTitle = false;
                  for (const title of allTitlesInMarkdown) {
                    if (lowerTrimmed === title) {
                      isTitle = true;
                      break;
                    }
                  }
                  if (!isTitle) {
                    paragraph = trimmed.length > 300 ? trimmed.substring(0, 300) + '...' : trimmed;
                    break;
                  }
                }
              }
            }
            
            // Final fallback: use first 300 chars of content if still no paragraph
            if (!paragraph && contentText.length > 0) {
              const fallbackText = contentText.replace(/\n+/g, ' ').trim();
              paragraph = fallbackText.length > 300 ? fallbackText.substring(0, 300) + '...' : fallbackText;
            }
          }
        }
        
        // Fallback to original line-based method if title not found in plaintext
        if (!paragraph && contentStart < contentEnd) {
          const contentLines = plaintextLines.slice(contentStart, contentEnd);
          const contentText = contentLines.join('\n').trim();
          
          if (contentText) {
            const allParagraphs = contentText.split(/\n\n+/);
            
            for (const p of allParagraphs) {
              const trimmed = p.trim();
              if (trimmed.length >= 20) {
                const lowerTrimmed = trimmed.toLowerCase();
                let isTitle = false;
                for (const title of allTitlesInMarkdown) {
                  if (lowerTrimmed === title) {
                    isTitle = true;
                    break;
                  }
                }
                if (!isTitle) {
                  paragraph = trimmed.length > 300 ? trimmed.substring(0, 300) + '...' : trimmed;
                  break;
                }
              }
            }
            
            // Final fallback
            if (!paragraph && contentText.length > 0) {
              paragraph = contentText.length > 300 ? contentText.substring(0, 300) + '...' : contentText;
            }
          }
        }
      }

      matchingTitles.push({
        title: {
          html: null,
          plaintext: titleText,
        },
        paragraph,
        score,
        index: i,
      });
    }
  }

  // Return the best match (highest score, or first if tie)
  if (matchingTitles.length === 0) {
    return { title: null, paragraph: '' };
  }

  // Sort by score (highest first), then by index (earliest first)
  matchingTitles.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.index - b.index;
  });

  const bestMatch = matchingTitles[0];
  return {
    title: bestMatch.title,
    paragraph: bestMatch.paragraph,
  };
}

// Helper function to trim paragraph containing search term
// Uses fuzzy matching to find related terms if exact match not found
export function trimParagraphContainingTerm(
  text: string,
  searchTerm: string,
  contextLength: number = 150,
  matchPosition?: number // Optional: specific position of the match to use instead of first occurrence
): string {
  const lowerText = text.toLowerCase();
  const lowerSearchTerm = searchTerm.toLowerCase();
  let index = -1;

  // If matchPosition is provided, use that specific position
  if (matchPosition !== undefined && matchPosition >= 0) {
    // Verify this position actually contains the search term (exact phrase)
    if (lowerText.substring(matchPosition, matchPosition + lowerSearchTerm.length) === lowerSearchTerm) {
      index = matchPosition;
    } else {
      // If not exact phrase at this position, it might be a word match
      // Find the word that matches at this position
      const searchWords = lowerSearchTerm.split(/\s+/).filter(w => w.length > 0);
      let foundWordAtPosition = false;
      
      for (const word of searchWords) {
        const wordStart = matchPosition;
        const wordEnd = wordStart + word.length;
        if (wordEnd <= lowerText.length && lowerText.substring(wordStart, wordEnd) === word) {
          // Found a word match at this position - use it
          index = wordStart;
          foundWordAtPosition = true;
          break;
        }
      }
      
      // If no word found at exact position, search nearby (within 20 chars)
      if (!foundWordAtPosition) {
        const searchStart = Math.max(0, matchPosition - 20);
        const searchEnd = Math.min(lowerText.length, matchPosition + 20);
        const nearbyText = lowerText.substring(searchStart, searchEnd);
        
        for (const word of searchWords) {
          const wordIndex = nearbyText.indexOf(word);
          if (wordIndex !== -1) {
            index = searchStart + wordIndex;
            foundWordAtPosition = true;
            break;
          }
        }
      }
      
      // Last resort: use the provided position anyway
      if (!foundWordAtPosition) {
        index = matchPosition;
      }
    }
  } else {
    // Default: find first occurrence
    index = lowerText.indexOf(lowerSearchTerm);
  }

  // If exact match not found, try fuzzy matching with expanded terms
  if (index === -1) {
    const terms = extractTerms(searchTerm);
    if (terms.length > 0) {
      const expanded = expandTerms(terms, searchTerm);

      // Try to find any expanded term in the original text (case-insensitive)
      for (const term of expanded) {
        if (!term || term.trim().length === 0) continue;

        // Escape special regex characters
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'i');
        const match = regex.exec(text);

        if (match) {
          index = match.index;
          break;
        }
      }
    }

    // If still no match found, return first part of text
    if (index === -1) {
      return text.substring(0, contextLength * 2).trim();
    }
  }

  // Find sentence boundaries around the match
  const start = Math.max(0, index - contextLength);
  const end = Math.min(text.length, index + searchTerm.length + contextLength);

  // Try to start at a sentence boundary
  let actualStart = start;
  for (let i = start; i < index; i++) {
    if (text[i] === '.' || text[i] === '!' || text[i] === '?') {
      if (
        i + 1 < text.length &&
        (text[i + 1] === ' ' || text[i + 1] === '\n')
      ) {
        actualStart = i + 2;
        break;
      }
    }
  }

  // Try to end at a sentence boundary
  let actualEnd = end;
  for (let i = index + searchTerm.length; i < end; i++) {
    if (text[i] === '.' || text[i] === '!' || text[i] === '?') {
      if (
        i + 1 < text.length &&
        (text[i + 1] === ' ' || text[i + 1] === '\n')
      ) {
        actualEnd = i + 1;
        break;
      }
    }
  }

  let trimmed = text.substring(actualStart, actualEnd).trim();

  // Add ellipsis if needed
  if (actualStart > 0) trimmed = '...' + trimmed;
  if (actualEnd < text.length) trimmed = trimmed + '...';

  return trimmed;
}

// Helper to extract preview content from features
export function extractPreviewFromFeatures(
  features: any[] | null | undefined
): {
  previewContent: string;
  previewHtml: string | null;
  previewPlaintext: string | null;
} {
  let previewContent = '';
  let previewHtml: string | null = null;
  let previewPlaintext: string | null = null;

  if (features) {
    for (const feature of features) {
      if (isContentFeature(feature) && feature.content?.[0]) {
        const firstContent = feature.content[0];
        previewContent = extractFirstParagraph(firstContent.plaintext || '');
        previewHtml = firstContent.html || null;
        previewPlaintext = firstContent.plaintext || null;
        break;
      }
    }
  }

  return { previewContent, previewHtml, previewPlaintext };
}

// Helper to find the title closest to where a search term appears in content
export function findTitleClosestToSearchTerm(
  markdown: string | null | undefined,
  plaintext: string | null | undefined,
  html: string | null | undefined,
  searchTerm: string
): { html: string | null; plaintext: string } | null {
  if (!markdown || !plaintext) return null;

  const lowerSearchTerm = searchTerm.toLowerCase();
  const lowerPlaintext = plaintext.toLowerCase();

  // Find the position of the search term in plaintext
  const searchIndex = lowerPlaintext.indexOf(lowerSearchTerm);
  if (searchIndex === -1) return null;

  const markdownLines = markdown.split('\n');
  const plaintextLines = plaintext.split('\n');

  // Find which line the search term appears on in plaintext
  let searchLineIndex = 0;
  let currentPos = 0;
  for (let i = 0; i < plaintextLines.length; i++) {
    const lineLength = plaintextLines[i].length + 1; // +1 for newline
    if (currentPos + lineLength > searchIndex) {
      searchLineIndex = i;
      break;
    }
    currentPos += lineLength;
  }

  // Find all titles and their line numbers
  const titles: Array<{
    title: { html: string | null; plaintext: string };
    lineIndex: number;
  }> = [];

  for (let i = 0; i < markdownLines.length; i++) {
    const line = markdownLines[i].trim();
    if (!line) continue;

    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      const titleText = headerMatch[1]
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
        .trim();

      // Verify HTML header if available
      if (html) {
        const htmlHeaderRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi;
        let found = false;
        let match;

        while ((match = htmlHeaderRegex.exec(html)) !== null) {
          const headerText = striptags(match[1]).trim();
          const normalizedMarkdown = titleText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();
          const normalizedHtml = headerText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();

          if (
            normalizedHtml === normalizedMarkdown ||
            normalizedHtml.includes(normalizedMarkdown) ||
            normalizedMarkdown.includes(normalizedHtml)
          ) {
            found = true;
            break;
          }
        }

        if (!found) continue;
      }

      titles.push({
        title: {
          html: null,
          plaintext: titleText,
        },
        lineIndex: i,
      });
    }
  }

  if (titles.length === 0) return null;

  // Find the title closest to the search term line
  // Prefer titles that come before the search term
  let closestTitle: { html: string | null; plaintext: string } | null = null;
  let closestDistance = Infinity;

  for (const titleInfo of titles) {
    // Map markdown line index to approximate plaintext line index
    // This is an approximation since markdown and plaintext lines might not align perfectly
    const titleLineIndex = Math.min(
      titleInfo.lineIndex,
      plaintextLines.length - 1
    );
    const distance = searchLineIndex - titleLineIndex;

    // Prefer titles that come before the search term
    if (distance >= 0 && distance < closestDistance) {
      closestDistance = distance;
      closestTitle = titleInfo.title;
    }
  }

  // If no title before the search term, use the closest one after
  if (!closestTitle) {
    closestDistance = Infinity;
    for (const titleInfo of titles) {
      const titleLineIndex = Math.min(
        titleInfo.lineIndex,
        plaintextLines.length - 1
      );
      const distance = titleLineIndex - searchLineIndex;
      if (distance >= 0 && distance < closestDistance) {
        closestDistance = distance;
        closestTitle = titleInfo.title;
      }
    }
  }

  // If still no title found, use the last title (most recent)
  if (!closestTitle && titles.length > 0) {
    closestTitle = titles[titles.length - 1].title;
  }

  return closestTitle;
}

// Helper to determine result title for content match
export function determineResultTitle(
  feature: ContentFeatureModel,
  section: SectionModel,
  contentItem?: {
    markdown?: string | null;
    plaintext?: string | null;
    html?: string | null;
  },
  searchTerm?: string
): { html: string | null; plaintext: string } {
  let resultTitle: { html: string | null; plaintext: string } = {
    html: null,
    plaintext: '',
  };

  // 1. Try feature title
  const featureTitleText = feature.title?.plaintext?.trim() || '';
  if (featureTitleText) {
    resultTitle = {
      html: feature.title?.html || null,
      plaintext: featureTitleText,
    };
  }

  // 2. If we have a content item and search term, find the title that comes BEFORE the search term
  // Priority: title before search term in content -> feature title -> section title
  if (
    contentItem &&
    searchTerm &&
    contentItem.markdown &&
    contentItem.plaintext
  ) {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerPlaintext = contentItem.plaintext.toLowerCase();
    const searchIndex = lowerPlaintext.indexOf(lowerSearchTerm);

    if (searchIndex !== -1) {
      // Find all titles in the content and their character positions in plaintext
      const titles: Array<{
        title: { html: string | null; plaintext: string };
        charIndex: number;
      }> = [];

      // Use HTML to find exact positions of headings in the plaintext
      if (contentItem.html) {
        const htmlHeaderRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi;
        const htmlMatches: Array<{ text: string; htmlIndex: number }> = [];
        let match;

        // First, collect all HTML headings with their positions in HTML
        while ((match = htmlHeaderRegex.exec(contentItem.html)) !== null) {
          const headerText = striptags(match[1]).trim();
          if (!headerText) continue;
          htmlMatches.push({
            text: headerText,
            htmlIndex: match.index,
          });
        }

        // Sort by HTML position to maintain order
        htmlMatches.sort((a, b) => a.htmlIndex - b.htmlIndex);

        // Now find each heading's position in plaintext, searching sequentially
        let lastFoundIndex = 0;
        for (const htmlMatch of htmlMatches) {
          const headerText = htmlMatch.text;
          const headerTextLower = headerText.toLowerCase();

          // Search for the heading text in plaintext, starting from where we last found a heading
          // This ensures we find headings in order
          const normalizedHeader = headerTextLower.replace(/\s+/g, ' ').trim();
          const searchPattern = normalizedHeader.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&'
          );
          const regex = new RegExp(searchPattern, 'i');

          // Search from lastFoundIndex to ensure we get headings in order
          const searchText = contentItem.plaintext.substring(lastFoundIndex);
          const plaintextMatch = regex.exec(searchText);

          if (plaintextMatch) {
            const charIndex = lastFoundIndex + plaintextMatch.index;
            titles.push({
              title: {
                html: null,
                plaintext: headerText,
              },
              charIndex,
            });
            lastFoundIndex = charIndex + headerText.length;
          }
        }
      } else {
        // Fallback: use markdown to find titles and approximate positions
        const markdownLines = contentItem.markdown.split('\n');
        const plaintextLines = contentItem.plaintext.split('\n');
        let currentCharIndex = 0;

        for (let i = 0; i < markdownLines.length; i++) {
          const line = markdownLines[i].trim();
          if (!line) {
            if (i < plaintextLines.length) {
              currentCharIndex += plaintextLines[i].length + 1;
            }
            continue;
          }

          const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
          if (headerMatch) {
            const titleText = headerMatch[1]
              .replace(/\*\*/g, '')
              .replace(/\*/g, '')
              .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
              .trim();

            // Try to find this title in plaintext
            const titleTextLower = titleText.toLowerCase();
            const plaintextLower = contentItem.plaintext.toLowerCase();
            const titleIndex = plaintextLower.indexOf(
              titleTextLower,
              currentCharIndex
            );

            if (titleIndex !== -1) {
              titles.push({
                title: {
                  html: null,
                  plaintext: titleText,
                },
                charIndex: titleIndex,
              });
            }
          }

          if (i < plaintextLines.length) {
            currentCharIndex += plaintextLines[i].length + 1;
          }
        }
      }

      // Sort titles by their position to ensure correct order
      titles.sort((a, b) => a.charIndex - b.charIndex);

      // Find the title that comes BEFORE the search term (closest before)
      let titleBefore: { html: string | null; plaintext: string } | null = null;
      let closestBeforeDistance = Infinity;

      for (const titleInfo of titles) {
        // Only consider titles that come BEFORE the search term
        if (titleInfo.charIndex < searchIndex) {
          const distance = searchIndex - titleInfo.charIndex;
          if (distance < closestBeforeDistance) {
            closestBeforeDistance = distance;
            titleBefore = titleInfo.title;
          }
        }
      }

      // If we found a title before the search term, use it
      // Otherwise, keep the feature title (at position 0, before everything) or it will fall through to section title
      if (titleBefore && titleBefore.plaintext) {
        resultTitle = titleBefore;
      }
      // If no title before in content:
      // - If feature title exists, it's already set (it's at position 0, before everything)
      // - If no feature title, it will fall through to section title
    }
  }

  // 3. If still empty (no feature title and no content title before search term), use section title
  if (!resultTitle.plaintext && section.sectionTitle?.plaintext)
    resultTitle = {
      html: section.sectionTitle.html || null,
      plaintext: section.sectionTitle.plaintext.trim(),
    };

  // 4. Fallback to sectionId
  if (!resultTitle.plaintext)
    resultTitle = {
      html: null,
      plaintext: section.sectionId || '',
    };

  return resultTitle;
}
